import React from "react";

import { useMutation } from "@apollo/react-hooks";
import { Tabs } from "antd";
import gql from "graphql-tag";

import { GlobalStyleVariables } from "../../../cssConstants";
import { H5 } from "../../common/StyledComponents";
import { InternalDataSourceProviders } from "../common";
import { DataSource } from "../DataSourceTable";

import { DataSourceSyncPanel } from "./DataSourceSyncPanel/DataSourceSyncPanel";
import { FunctionsPanel } from "./FunctionsPanel/FunctionsPanel";
import ManageCredentialsTab from "./ManageCredentialsTab";
import * as styled from "./styledComponents";

interface Props {
  onClose: () => void;
  isVisible: boolean;
  loading: boolean;
  dataSource: DataSource | undefined;
}

interface DrawerContentProps {
  dataSource: DataSource;
  onClose: () => void;
  loading: boolean;
}

export const RENAME_DATA_SOURCE_MUTATION = gql`
  mutation RenameDataSourceMutation($dataSourceId: ID!, $name: String!) {
    renameDataSource(dataSourceId: $dataSourceId, name: $name) {
      ok
      dataSource {
        id
        name
      }
    }
  }
`;

export const DrawerWithEnvironmentCredentials = ({
  dataSource,
  onClose,
  loading
}: DrawerContentProps) => {
  const [rename] = useMutation(RENAME_DATA_SOURCE_MUTATION);
  const renameDataSource = (name: string) => {
    rename({
      variables: {
        dataSourceId: dataSource.id,
        name: name
      }
    });
  };

  const isInternalDataSource = InternalDataSourceProviders.has(dataSource.integration);

  return (
    <>
      <styled.HeaderWithDrawerPadding>
        <div>{dataSource.integrationTitle}</div>
        <styled.HeaderInlineInput
          data-test="headerInline"
          includeEditIcon={!isInternalDataSource}
          isValidValue={e => e.trim().length > 0}
          onChange={e => renameDataSource(e.target.value)}
          value={dataSource.name}
        />
      </styled.HeaderWithDrawerPadding>
      <styled.StyledTabs defaultActiveKey="1">
        <Tabs.TabPane tab="Environments" key="1">
          <styled.DrawerSidePadding>
            {isInternalDataSource ? (
              <H5>
                System data sources work in all environments and can not be deleted
              </H5>
            ) : (
              <ManageCredentialsTab dataSource={dataSource} onClose={onClose} />
            )}
          </styled.DrawerSidePadding>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Functions" key="2">
          <styled.FunctionTabPadding>
            {dataSource.supportsSync && !isInternalDataSource && (
              <DataSourceSyncPanel loading={loading} dataSource={dataSource} />
            )}
            <FunctionsPanel dataSource={dataSource} />
          </styled.FunctionTabPadding>
        </Tabs.TabPane>
      </styled.StyledTabs>
    </>
  );
};

const DataSourceDrawer = ({ onClose, loading, isVisible, dataSource }: Props) => (
  <styled.DrawerNoPadding
    width={GlobalStyleVariables.drawerWidth}
    placement="right"
    closable={false}
    onClose={onClose}
    visible={isVisible}
  >
    {loading ? (
      <styled.Skeleton loading active />
    ) : (
      dataSource && (
        <DrawerWithEnvironmentCredentials
          dataSource={dataSource}
          loading={loading}
          onClose={onClose}
        />
      )
    )}
  </styled.DrawerNoPadding>
);

export default DataSourceDrawer;
